import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';

import { PhotoUploadForm } from '@vlabs/shared/components/photo-upload-form';
import { getFaceDetectionsFromFile } from '@vlabs/shared/requests/getFaceDetectionsFromFile';
import type { TDetection } from '@vlabs/shared/requests/getFaceDetectionsFromFile';
import { Control, Page, useModal } from '@vlabs/uikit';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { buildMatchList } from './buildMatchList';
import st from './FaceCreateForm.module.sass';
import { FaceCreateContext } from './FaceCreateFormContext';
import { IsoQualityCheckForm } from './IsoQualityCheckForm';
import FaceMatchModal from '../components/FaceMatchModal';
import MatchList from '../components/MatchList';
import type {
  FaceCreateFormProps, MatchByExternalId, MatchByListlId, IsoResult, Card, TFile,
} from '../types';

export function FaceCreateForm({
  noSelect = false,
  formFieldComponent = <IsoQualityCheckForm noSelect={noSelect} />,
  onSubmit,
  defaultValues,
  closeModal,
  context = undefined,
}: FaceCreateFormProps): React.JSX.Element {
  const { t } = useTranslation();

  const faceMatchModal = useModal({ size: 'small' });

  const [sampleId, setSampleId] = useState<string[]>([]);
  const [detections, setDetections] = useState<TDetection[]>([]);
  const [isoResult, setIsoResult] = useState<IsoResult | null>(null);
  const [shouldCheckISO, setShouldCheckISO] = useState<boolean>(false);
  const [card, setCard] = useState<Card | null>(null);
  const [matchByExternalId, setMatchByExternalId] = useState<MatchByExternalId[]>([]);
  const [matchByListId, setMatchByListId] = useState<MatchByListlId[]>([]);

  const form = useForm({ defaultValues });
  const file = useWatch({ name: 'file', control: form.control });

  const externalIdTitle = t('monokit:create face modal.forms.match modal.header.title match');
  const externalIdMessage = t('monokit:create face modal.forms.match modal.message.title match');
  const listIdTitle = t('monokit:create face modal.forms.match modal.header.title duplicate');
  const listIdMessage = t('monokit:create face modal.forms.match modal.message.title duplicate');

  useEffect(() => {
    if (!file) return;

    const fetchFaceDetections = async () => {
      const faceDetections = await getFaceDetectionsFromFile(file.raw);
      if (faceDetections.length) {
        setDetections(faceDetections);
      } else {
        form.setError('file', { message: t('monokit:create face modal.forms.selected image does not contain faces') });
        form.setValue('file', undefined);
        setDetections([]);
        setSampleId([]);
      }
    };

    fetchFaceDetections();
  }, [file, form, t]);

  const onFileSelect = (newFile: TFile) => {
    if (!newFile) return;
    form.setValue('file', newFile);
  };

  const onResetFile = useCallback(() => {
    form.clearErrors('file');
    form.setValue('file', undefined);
    setDetections([]);
    setSampleId([]);
  }, [form]);

  const createFace = useCallback(async (values: Card) => {
    if (!sampleId?.length) {
      form.setError('file', { message: t('monokit:create face modal.forms.one face must be selected') });
      return;
    }

    if (values.external_id || values.lists?.length) {
      const { externalIdMatches, listIdMatches } = await buildMatchList({ formValues: values, detections, sampleId });
      if (externalIdMatches.length && !listIdMatches.length) {
        setCard(values);
        setMatchByExternalId(externalIdMatches);
        faceMatchModal.open();
        return;
      }
      if (context !== 'card' && listIdMatches.length && !externalIdMatches.length) {
        setCard(values);
        setMatchByListId(listIdMatches);
        faceMatchModal.open();
        return;
      }
    }

    onSubmit({ ...values, sample_id: sampleId });
  }, [onSubmit, sampleId, detections, form, faceMatchModal, t]);

  const formContext = useMemo(() => {
    return {
      detections,
      sampleId,
      isoResult,
      setIsoResult,
      shouldCheckISO,
      setShouldCheckISO,
    };
  }, [
    detections,
    sampleId,
    isoResult,
    setIsoResult,
    shouldCheckISO,
    setShouldCheckISO,
  ]);

  const isSubmitButtonDisabled = !file
    || !detections.length
    || (shouldCheckISO && !isoResult?.status)
    || (detections?.length > 1 && !sampleId.length);

  return (
    <FormProvider {...form}>
      <FaceCreateContext.Provider value={formContext}>
        <form onSubmit={form.handleSubmit(createFace)}>
          <Page
            className={st.Root}
            title={t('monokit:create face modal.forms.title')}
          >
            {faceMatchModal.wrap(
              <FaceMatchModal
                closeModal={faceMatchModal.close}
                message={matchByExternalId?.length ? externalIdMessage : listIdMessage}
                onClick={() => {
                  onSubmit({ ...card, sample_id: sampleId });
                  faceMatchModal.close();
                }}
                title={matchByExternalId?.length ? externalIdTitle : listIdTitle}
              >
                {matchByExternalId?.length
                  ? (
                    <MatchList
                      externalIdMatches={matchByExternalId}
                      similarityTagKind="negative"
                    />
                  )
                  : (
                    <MatchList
                      listIdMatches={matchByListId}
                      similarityTagKind="negative"
                    />
                  )}
              </FaceMatchModal>,
            )}
            <div className={st.ColumnsContainer}>
              <div className={st.PhotoColumns}>
                <div className={st.PhotoUploadColumn}>
                  <div className={st.PhotoUploadForm}>
                    <PhotoUploadForm
                      data-testid="FaceCreateForm.PhotoUploadForm"
                      detections={detections}
                      errorProps={{ errors: form.formState.errors, register: form.register }}
                      file={file}
                      onFileSelect={onFileSelect}
                      onResetFile={onResetFile}
                      onSelectSampleId={(id: string[]) => setSampleId(id)}
                    />
                  </div>
                  {!!file && !detections?.length && <span>{t('monokit:create face modal.forms.detections.detection in progress')}</span>}
                  {!!detections?.length && (
                    <span data-testid="detectionSelector.count">
                      {t('monokit:create face modal.forms.detections.detection', { count: detections.length })}
                    </span>
                  )}
                </div>
                {formFieldComponent}
              </div>
              <div className={st.Separator} />
              <div className={st.ModalFooter}>
                <div className={st.ButtonWrapper}>
                  <Control.Button
                    data-testid="FaceCreateForm.button.submit"
                    disabled={isSubmitButtonDisabled}
                    fullWidth
                    type="submit"
                  >
                    {t('monokit:create face modal.forms.buttons.submit')}
                  </Control.Button>
                </div>
                <div className={st.ButtonWrapper}>
                  <Control.Button
                    data-testid="FaceCreateForm.button.close modal"
                    fullWidth
                    kind="secondary"
                    onClick={closeModal}
                    variant="outlined"
                  >
                    {t('monokit:create face modal.forms.buttons.cancel')}
                  </Control.Button>
                </div>
              </div>
            </div>
          </Page>
        </form>
      </FaceCreateContext.Provider>
    </FormProvider>
  );
}
